import { Controller } from '@hotwired/stimulus'
import { Html5Qrcode } from 'html5-qrcode'

export default class extends Controller {
  static targets = ['reader', 'status']
  static values = {
    verbose: { type: Boolean, default: false },
    autoStart: { type: Boolean, default: true }
  }

  connect () {
    this.html5QrCode = null
    this.isScanning = false
    this.log('[QRScanner] Controller connected')
    this.log('[QRScanner] Reader target found:', this.hasReaderTarget)
    this.log('[QRScanner] Status target found:', this.hasStatusTarget)
    this.log('[QRScanner] Reader element:', document.getElementById('qr-reader'))

    if (this.autoStartValue) {
      this.log('[QRScanner] Auto-start enabled, initializing scanner...')
      // Small delay to ensure DOM is fully ready
      setTimeout(() => {
        this.log('[QRScanner] Starting scanner after delay')
        this.startScanner()
      }, 100)
    }
  }

  disconnect () {
    this.log('[QRScanner] Controller disconnecting')
    this.stopScanner()
  }

  async startScanner () {
    if (this.isScanning) {
      this.log('[QRScanner] Already scanning')
      return
    }

    try {
      this.updateStatus('Initializing camera...')
      this.log('[QRScanner] Starting scanner')

      this.html5QrCode = new Html5Qrcode('qr-reader')

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      }

      // Try multiple camera strategies in order
      const cameraStrategies = [
        { name: 'rear camera', constraint: { facingMode: 'environment' } },
        { name: 'front camera', constraint: { facingMode: 'user' } },
        { name: 'any camera', constraint: {} }
      ]

      let cameraStarted = false

      for (const strategy of cameraStrategies) {
        try {
          this.log(`[QRScanner] Attempting to start with ${strategy.name}`, strategy.constraint)
          await this.html5QrCode.start(
            strategy.constraint,
            config,
            this.onScanSuccess.bind(this),
            this.onScanFailure.bind(this)
          )
          this.log(`[QRScanner] Successfully started with ${strategy.name}`)
          cameraStarted = true
          break // Success! Exit loop
        } catch (strategyErr) {
          this.log(`[QRScanner] ${strategy.name} failed:`, strategyErr.message || strategyErr)
          // Continue to next strategy
        }
      }

      if (!cameraStarted) {
        throw new Error('All camera strategies failed. Unable to access any camera.')
      }

      this.isScanning = true
      this.updateStatus('Ready to scan!')
      this.log('[QRScanner] Scanner started successfully')
    } catch (err) {
      this.log('[QRScanner] Error starting scanner:', err)
      this.handleStartError(err)
    }
  }

  async stopScanner () {
    if (!this.html5QrCode || !this.isScanning) {
      return
    }

    try {
      this.log('[QRScanner] Stopping scanner')
      await this.html5QrCode.stop()
      this.html5QrCode.clear()
      this.html5QrCode = null
      this.isScanning = false
      this.updateStatus('Scanner stopped')
      this.log('[QRScanner] Scanner stopped successfully')
    } catch (err) {
      this.log('[QRScanner] Error stopping scanner:', err)
    }
  }

  onScanSuccess (decodedText, decodedResult) {
    this.log('[QRScanner] Scan success:', decodedText)
    this.updateStatus('Verifying QR code...')

    this.stopScanner().then(() => {
      this.navigateToUrl(decodedText)
    })
  }

  onScanFailure () {
    // This fires continuously while scanning, so we don't log it
    // to avoid console spam. Error parameter intentionally unused.
  }

  async navigateToUrl (scannedText) {
    try {
      this.log('[QRScanner] Verifying URL with backend:', scannedText)
      const result = await this.verifyUrlWithBackend(scannedText)

      if (result.valid) {
        this.log('[QRScanner] URL verified, navigating to:', result.url)
        this.updateStatus('Verified! Navigating...')
        window.location.href = result.url
      } else {
        this.log('[QRScanner] URL rejected:', result.error)
        this.updateStatus(result.error, true)
        setTimeout(() => {
          this.startScanner()
        }, 3000)
      }
    } catch (err) {
      this.log('[QRScanner] Verification failed:', err)
      this.updateStatus('Error verifying QR code', true)
      setTimeout(() => {
        this.startScanner()
      }, 3000)
    }
  }

  async verifyUrlWithBackend (url) {
    const ecosystemId = this.getCurrentEcosystemId()
    const endpoint = `/scan/${ecosystemId}/verify_url`

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({ url }),
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      return await response.json()
    } catch (err) {
      clearTimeout(timeoutId)

      if (err.name === 'AbortError') {
        return {
          valid: false,
          error: 'Verification timed out. Please try again.'
        }
      }

      throw err
    }
  }

  getCurrentEcosystemId () {
    return document.querySelector('meta[name="ecosystem_id"]')?.content
  }

  getCsrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  handleStartError (err) {
    const errorMsg = err.message || err.toString()

    if (errorMsg.includes('Permission denied') ||
        errorMsg.includes('NotAllowedError')) {
      this.updateStatus(
        'Camera permission denied. Please enable camera access.',
        true
      )
    } else if (errorMsg.includes('NotFoundError') ||
               errorMsg.includes('No camera')) {
      this.updateStatus(
        'No camera found on this device.',
        true
      )
    } else if (errorMsg.includes('NotReadableError') ||
               errorMsg.includes('in use')) {
      this.updateStatus(
        'Camera is already in use by another application.',
        true
      )
    } else {
      this.updateStatus(
        `Error starting camera: ${errorMsg}`,
        true
      )
    }
  }

  updateStatus (message, isError = false) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      if (isError) {
        this.statusTarget.classList.add('text-custom-red')
        this.statusTarget.classList.remove('text-neutral-700')
      } else {
        this.statusTarget.classList.remove('text-custom-red')
        this.statusTarget.classList.add('text-neutral-700')
      }
    }
  }

  log (...args) {
    if (this.verboseValue) {
      console.log(...args)
    }
  }
}
